import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TCampaignApi } from '@pages/campaigns/campaigns.interface';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { map, startWith } from 'rxjs';
import {
  IProfileMultiSelectState,
  ProfileMultiSelectFilterService,
} from '../profile-general-filters/profile-multi-select-filter.service';
import { ICampaignFilterOption } from './profile-campaign-filter.interface';

/**
 * The "Campaign" Scope filter for creator-details' Deliverables tab - the multi-campaign
 * counterpart of ProfileGeneralFiltersComponent (which filters by creator within one campaign).
 * Here it's the reverse: one creator, many campaigns, so options come from the creator's own
 * campaign list (passed in via `campaigns`) rather than `campaignAnalytics().creators`.
 */
@Component({
  imports: [MatButtonModule, MatFormFieldModule, MatSelectModule, NgxSkeletonLoaderModule, TranslateModule],
  selector: 'app-profile-campaign-filter',
  templateUrl: './profile-campaign-filter.component.html',
})
export class ProfileCampaignFilterComponent {
  private readonly multiSelectFilterService = inject(ProfileMultiSelectFilterService);
  private readonly translate = inject(TranslateService);

  public readonly campaigns = input<TCampaignApi[]>([]);
  public readonly initialSelectedCampaignIds = input<Set<string>>(new Set());
  public readonly loading = input.required<boolean>();

  public readonly campaignIdsChanged = output<Set<string>>();
  public readonly panelClosed = output<void>();
  public readonly selectionNormalized = output<Set<string>>();
  public readonly selectionState = signal<IProfileMultiSelectState<string>>(
    this.multiSelectFilterService.createUnfilteredSelectionState<string>(),
  );
  private isPanelOpen = false;
  private panelSelectionSnapshot: string | null = null;
  public readonly languageChange = toSignal(
    this.translate.onLangChange.pipe(
      map((event) => event.lang),
      startWith(this.translate.currentLang),
    ),
    { initialValue: this.translate.currentLang },
  );

  public readonly campaignOptions = computed((): ICampaignFilterOption[] => {
    return this.campaigns().map((campaign) => ({ id: campaign.id, name: campaign.name }));
  });

  public readonly totalCampaignCount = computed(() => this.campaignOptions().length);

  public readonly selectedCampaignIds = computed(() => new Set(this.selectionState().uiSelectedValues));

  public readonly emptyLabel = computed(() => {
    this.languageChange();

    const totalCampaignCount = this.totalCampaignCount();

    if (totalCampaignCount === 0) {
      return this.translate.instant('pages.creatorDetails.deliverables.filter.allCampaigns');
    }

    return `${this.translate.instant('pages.creatorDetails.deliverables.filter.allCampaigns')} (${totalCampaignCount})`;
  });

  public readonly triggerLabel = computed(() => {
    this.languageChange();

    const orderedSelectedCampaigns = this.campaignOptions().filter((campaign) =>
      this.selectedCampaignIds().has(campaign.id),
    );

    return this.multiSelectFilterService.buildSelectionSummary(
      orderedSelectedCampaigns[0]?.name ?? '',
      orderedSelectedCampaigns.length,
      (this.translate.instant('shared.more') as string).toLowerCase(),
    );
  });

  constructor() {
    effect(() => {
      if (this.loading()) {
        this.isPanelOpen = false;
      }

      const availableCampaignIds = this.campaignOptions().map((campaign) => campaign.id);
      const nextState: IProfileMultiSelectState<string> = this.multiSelectFilterService.syncSelectionState(
        availableCampaignIds,
        this.initialSelectedCampaignIds(),
        this.selectionState(),
      );

      if (!this.selectionStatesAreEqual(this.selectionState(), nextState)) {
        this.selectionState.set(nextState);

        const normalizedCampaignIds = this.emitCampaignIds();

        if (!this.isPanelOpen) {
          this.selectionNormalized.emit(normalizedCampaignIds);
        }
      }
    });
  }

  public onSelectionChange(event: MatSelectChange): void {
    const nextSelectedCampaignIds: string[] = event.value ?? [];
    const nextState: IProfileMultiSelectState<string> =
      nextSelectedCampaignIds.length === 0
        ? this.multiSelectFilterService.createUnfilteredSelectionState<string>()
        : this.multiSelectFilterService.createExplicitSelectionState(nextSelectedCampaignIds);

    this.selectionState.set(nextState);
    this.emitCampaignIds();
  }

  public onSelectAll(event: Event): void {
    event.stopPropagation();

    this.selectionState.set(
      this.multiSelectFilterService.createExplicitSelectionState(this.campaignOptions().map((campaign) => campaign.id)),
    );
    this.emitCampaignIds();
  }

  public onUnselectAll(event: Event): void {
    event.stopPropagation();

    this.selectionState.set(this.multiSelectFilterService.createUnfilteredSelectionState<string>());
    this.emitCampaignIds();
  }

  public onOpenedChange(opened: boolean): void {
    this.isPanelOpen = opened;

    if (opened) {
      this.panelSelectionSnapshot = this.getSelectionSnapshot();
    }
  }

  public onPanelClosed(): void {
    const nextSelectionSnapshot = this.getSelectionSnapshot();
    const selectionChanged =
      this.panelSelectionSnapshot !== null && this.panelSelectionSnapshot !== nextSelectionSnapshot;

    this.panelSelectionSnapshot = null;

    if (!selectionChanged) {
      return;
    }

    this.panelClosed.emit();
  }

  private emitCampaignIds(): Set<string> {
    const normalizedCampaignIds = this.multiSelectFilterService.normalizeValuesForEmission(
      this.selectionState(),
      this.campaignOptions().map((campaign) => campaign.id),
    );

    this.campaignIdsChanged.emit(normalizedCampaignIds);

    return normalizedCampaignIds;
  }

  private getSelectionSnapshot(): string {
    return JSON.stringify(
      Array.from(
        this.multiSelectFilterService.normalizeValuesForEmission(
          this.selectionState(),
          this.campaignOptions().map((campaign) => campaign.id),
        ),
      ).sort(),
    );
  }

  private selectionStatesAreEqual(
    left: IProfileMultiSelectState<string>,
    right: IProfileMultiSelectState<string>,
  ): boolean {
    return (
      left.presentation === right.presentation &&
      left.uiSelectedValues.length === right.uiSelectedValues.length &&
      left.uiSelectedValues.every((value, index) => value === right.uiSelectedValues[index])
    );
  }
}
